import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import type { Game } from '../Game';
import { Bullet } from './Bullet';
import { Explosion } from './Explosion';
import { useStore } from '../../state/store';
import { interceptPoint, entityVelocity } from '../aim';
import { spawnWreckage, type WreckPalette } from './Wreckage';
import { Trail } from './Trail';
import {
  makeAlbatrosCockpit, makePfalzCockpit, makeSopwithCockpit,
  makeAlbatrosFull, makePfalzFull, makeSopwithFull,
  cockpitGuns, cockpitDash, makePropDisc,
} from './planeModels';

// Coordinated-turn flight model (quaternion — no gimbal lock, full loops preserved):
//   - Pitch: rotation about the body X axis (stick.y). Unbounded → loops work.
//   - Roll : stick.x seeks a TARGET BANK ANGLE (the plane banks and HOLDS, it doesn't
//            spin continuously) — releasing the stick auto-levels the wings.
//   - Turn : a world-Y yaw proportional to sin(bank) — banking IS turning, so sideways
//            input swings the nose through a coordinated turn instead of barrel-rolling.
// Per-plane stats map: pitchRate → pitch rate, rollRate → bank-seek gain, yawCouple →
// peak turn rate at full bank.
const SMOOTH_TAU = 0.08;
const MAX_BANK = 1.05;       // rad (~60°) — the bank angle a full sideways input seeks
const MAX_ROLL_RATE = 3.5;   // rad/s cap on how fast the wings roll toward the target bank
const BOOST_MULT = 3.0;      // afterburner speed multiplier (hold to rush across the map)
const BASE_FOV = 60;
const BOOST_FOV = 82;        // FOV widens under boost to sell the speed
// --- Energy / momentum flight (arcade nudge) -------------------------------------------------
// Airspeed is a live variable, not a constant: gravity trades speed for altitude. Climbing bleeds
// it toward a mush floor; diving builds an overspeed rush; thrust restores cruise in level flight.
// Everything is expressed as a fraction of each airframe's own cruise speed so all planes behave
// consistently. Tuned so a sustained climb past ~50° mushes — the nose sags through the horizon on
// its own and the plane recovers by diving. No spin, no rudder, still one-thumb flyable.
const GRAV_FRAC = 0.4;       // airspeed lost per second per unit sin(climb): ~40%/s pointing straight up
const THRUST_TAU = 2.0;      // s — how fast thrust restores the cruise target in level flight
const MUSH_FRAC = 0.35;      // below this fraction of cruise the elevator loses bite and the nose sags
const STALL_WARN_FRAC = 0.55; // below this the RPM gauge redlines and the stall horn starts (warns BEFORE mush)
const MIN_AIR_FRAC = 0.12;   // airspeed floor — the plane keeps mushing forward, never stops/reverses
const OVERSPEED_FRAC = 1.8;  // dive overspeed ceiling, as a fraction of the current cruise target
// Afterburner heat: continuous-boost seconds to trip, cool seconds to recover, hysteresis resume.
const BOOST_HEAT_UP = 1 / 4.5;   // fully heat-soaked after ~4.5 s of held boost
const BOOST_HEAT_DOWN = 1 / 6.0; // fully cool ~6 s after release
const BOOST_RESUME = 0.35;       // once tripped, locked out until heat cools back below this
const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);
const AXIS_Z = new THREE.Vector3(0, 0, 1);
const _qx = new THREE.Quaternion();
const _qy = new THREE.Quaternion();
const _qz = new THREE.Quaternion();
const _vRight = new THREE.Vector3();
// Targeting-loop scratch (see the auto-aim scan in update).
const _ppWp = new THREE.Vector3();
const _ppToE = new THREE.Vector3();
const _ppAimBest = new THREE.Vector3();
const _trailPt = new THREE.Vector3();
// Auto-aim cone widens as enemies get close.
const FIRE_CONE_DEG_FAR = 9;   // at >= FIRE_RANGE
const FIRE_CONE_DEG_NEAR = 28; // at <= 150 u
const FIRE_RANGE = 900;
const FIRE_NEAR = 150;
const REGEN_DELAY = 5;
// Ace bonuses applied ONLY to the unit the player personally controls — the carrot that
// makes taking the stick worthwhile, without touching (or weakening) the AI wingmen.
const ACE_FIRE_MULT = 0.6;      // reload 40% faster
const ACE_BULLET_DAMAGE = 2;    // each round does double an AI round's damage

export class PlayerPlane extends Entity {
  readonly forward = new THREE.Vector3(0, 0, -1);
  pitchRate = 0;
  private curRoll = 0; // roll rate applied this frame (for audio)
  private curTurn = 0; // turn rate applied this frame (for audio)
  private boostFactor = 0; // smoothed 0..1 — drives the ANALOG afterburner speed
  private _fovBoost = 0;   // smoothed 0..1 — drives ONLY the speed-rush FOV, off a committed boost
  private airspeed = 0;    // live m/s from the energy model (init to cruise V in ctor)
  private boostHeat = 0;   // 0..1 afterburner thermal load — tracked for the controlled plane only
  private overheated = false; // burner tripped; locked out until it cools to BOOST_RESUME
  private stallBeepTimer = 0; // counts down to the next stall-horn blip (cadence shortens with danger)
  fireMult = 1;      // <1 = Munitions Airdrop overcharge (faster reload); random events set/clear it
  jammed = false;    // Clockwork Gremlins random event — guns intermittently misfire while true

  // Per-plane stats, snapshotted from the store at construction.
  private V: number;
  private maxPitch: number;
  private rollGain: number;  // how aggressively roll seeks the target bank
  private maxTurn: number;   // peak world-yaw turn rate at full bank
  private fireInterval: number;
  private maxHpStat: number;
  private regenRate: number;

  /** Normalized maneuver intensity [0..1] from current rates — used by the audio system
   *  to rev the engine even when input comes from the AI autopilot. */
  getManeuverIntensity(): number {
    const mag = Math.hypot(
      this.pitchRate / this.maxPitch,
      this.curRoll / MAX_ROLL_RATE,
      this.curTurn / Math.max(0.01, this.maxTurn),
    );
    // Boost dominates the engine note when lit.
    return Math.min(1, Math.max(mag * 0.6, this.boostFactor));
  }
  hpFrac(): number { return THREE.MathUtils.clamp(this.hp / this.maxHpStat, 0, 1); }
  private livery: WreckPalette;
  /** Enemy the autopilot is currently pursuing (for the map's target line), even before
   *  it's in firing range. 0 when loitering / no target. */
  aiPursuitId = 0;
  /** Phase of an AI ground-attack run when strafing a tank (own per-fighter state). */
  gaPhase: 'approach' | 'dive' | 'climb' = 'approach';
  private trail = new Trail(46, 0xeaf2ff, 0.5);
  private _trailAdded = false;
  private fireCooldown = 0;
  private tumbleVel = new THREE.Vector3();
  private tumbleRot = new THREE.Vector3();
  camera: THREE.PerspectiveCamera | null = null;
  private cockpit: THREE.Group;
  /** Cached cockpit prop-blur disc, spun each frame. undefined = not yet searched; null = none. */
  private _propDisc?: THREE.Object3D | null;
  private fullModel: THREE.Group;
  private crashSmokeTimer = 0;
  private cameraDetached = false;
  /** Twin-gun firing: flips each shot so rounds alternate between the left and right barrel. */
  private _gunSide = -1;
  private _muzzleLocal = new THREE.Vector3();
  private deathOrbitAngle = 0;
  private deathOrbitHeight = 15;
  private impactLogged = false;

  /** Built uncontrolled (AI). The Game calls `possess(camera)` to take control.
   *  `planeOverride` lets allied wingmen use a different livery from the player's loadout. */
  constructor(planeOverride?: { id: string; cowling: number; wing: number }) {
    super('player');
    this.friendly = true;
    this.controllable = true;
    this.unitLabel = 'FIGHTER';
    // Snapshot the selected plane + perks for this run.
    const stats = useStore.getState().stats;
    this.V = stats.speed;
    this.airspeed = stats.speed; // start at cruise, not stalled
    this.maxPitch = stats.pitchRate;
    this.rollGain = stats.rollRate;   // reused as the bank-seek gain
    this.maxTurn = stats.yawCouple;   // reused as peak turn rate at full bank
    this.fireInterval = stats.fireInterval;
    this.maxHpStat = stats.maxHp;
    this.regenRate = stats.regenRate;
    this.hp = this.maxHpStat;
    this.radius = 4;
    // Fokker Dr.I OBB: ~7.2u wingspan, ~3.5u stack height (3 wings + gear), ~6u length.
    this.localBounds = new THREE.Box3(
      new THREE.Vector3(-3.7, -1.6, -3.0),
      new THREE.Vector3(3.7, 2.6, 4.0),
    );

    // Build the cockpit + exterior model. The player's own plane uses the selected
    // loadout; allied AI fighters can override the livery so they read as wingmen.
    const planeId = planeOverride?.id ?? useStore.getState().prog.selectedPlane;
    const colors = planeOverride
      ? { cowling: planeOverride.cowling, wing: planeOverride.wing }
      : { cowling: stats.cowlingColor, wing: stats.wingColor };
    // Debris livery for the crash wreckage — matches this plane's paint.
    this.livery = { body: colors.cowling, trim: colors.wing, metal: 0x3a3a38, dark: 0x161614, wood: 0x3a2a1a };
    switch (planeId) {
      case 'albatros':
        this.cockpit = makeAlbatrosCockpit(colors);
        this.fullModel = makeAlbatrosFull(colors);
        break;
      case 'pfalz':
        this.cockpit = makePfalzCockpit(colors);
        this.fullModel = makePfalzFull(colors);
        break;
      case 'sopwith':
        this.cockpit = makeSopwithCockpit(colors);
        this.fullModel = makeSopwithFull(colors);
        break;
      default: // fokker
        this.cockpit = makeCockpitGeometry(colors.cowling, colors.wing);
        this.fullModel = makeFokkerTriplane();
        break;
    }
    this.group.add(this.cockpit);
    this.group.add(this.fullModel);
    // Controlled = first-person cockpit; uncontrolled (allied AI) = exterior model so
    // wingmen look like planes from the outside. possess()/release() flip these.
    this.cockpit.visible = false;
    this.fullModel.visible = true;
    this.controlled = false;
  }

  /** Take control: attach the camera at the pilot's eye, show the cockpit. */
  possess(camera: THREE.PerspectiveCamera): void {
    this.controlled = true;
    this.camera = camera;
    this.group.add(camera);
    camera.position.set(0, 0.6, -0.3);
    camera.rotation.set(0, 0, 0);
    camera.fov = BASE_FOV;
    camera.updateProjectionMatrix();
    this.cockpit.visible = true;
    this.fullModel.visible = false;
    // Sync the HUD HP bar to this unit immediately on takeover.
    useStore.getState().setHp(Math.max(0, Math.ceil(this.hp)), this.maxHpStat);
  }

  /** Release control: detach the camera, revert to the exterior model + AI flight. */
  release(): void {
    this.controlled = false;
    if (this.camera && this.camera.parent === this.group) this.group.remove(this.camera);
    this.camera = null;
    this.cockpit.visible = false;
    this.fullModel.visible = true;
  }

  dispose(): void {
    this.trail.dispose();
    super.dispose();
  }

  /** Reset energy/thermal/stall to a clean cruise. Used when the UFO drops the plane back on the
   *  battlefield — it must not arrive stalled, hot, or with the RPM needle in the red. */
  resetEnergyState(): void {
    this.airspeed = this.V;
    this.boostFactor = 0;
    this._fovBoost = 0;
    this.boostHeat = 0;
    this.overheated = false;
    this.stallBeepTimer = 0;
    if (this.camera) { this.camera.fov = BASE_FOV; this.camera.updateProjectionMatrix(); }
    const s = useStore.getState();
    s.setBoostThermal(0, false);
    s.setFlightGauge(1, 0);
  }

  update(ctx: EntityCtx): void {
    const { game, dt, now } = ctx;
    const phase = useStore.getState().phase;

    // Only the unit that triggered the final game-over runs the crash cinematic.
    if (phase === 'gameover' && this.camera) {
      this.updateCrashSequence(ctx);
      return;
    }
    // An allied AI fighter during the game-over freeze just holds station.
    if (phase === 'gameover') return;

    // Keep the model in sync with control: first-person cockpit when you're flying it,
    // exterior airframe when it's an AI wingman. Enforced every frame so a jump can never
    // leave the camera buried inside the full exterior model ("inside the object").
    if (this.cockpit.visible !== this.controlled) this.cockpit.visible = this.controlled;
    if (this.fullModel.visible === this.controlled) this.fullModel.visible = !this.controlled;

    // Spin the cockpit prop-blur disc so it reads as a turning propeller, not a static smudge.
    if (this.controlled) {
      if (this._propDisc === undefined) {
        this._propDisc = null;
        this.cockpit.traverse((o) => { if (o.userData.propDisc) this._propDisc = o; });
      }
      if (this._propDisc) this._propDisc.rotation.z -= dt * 45; // fast blur
    }

    // Control source: the possessed unit uses the stick (or the AI toggle); every other
    // allied fighter flies on autopilot, hunting the nearest enemy. While the tactical map
    // is open the player isn't at the controls, so the unit autopilots too.
    const s = useStore.getState();
    const aiDriven = !this.controlled || s.ai || s.mapOpen;
    const stick = aiDriven ? autopilotStick(this, game) : game.input.getStick();
    const q = this.group.quaternion;

    // 0. Afterburner throttle + thermal load. Boost is analog (how far LT is pulled; touch/key =
    //    full). The burner heats while lit and cools when off; once it trips it's locked out until
    //    it cools back to BOOST_RESUME. Heat is only tracked for the plane you're flying — wingmen
    //    never boost, so this leaves them untouched.
    let boostAmt = Math.max(
      useStore.getState().boosting ? 1 : 0,
      game.input.gamepadBoostAmount(),
      game.input.keyBoostHeld() ? 1 : 0,
    );
    if (this.controlled) {
      if (this.overheated) {
        boostAmt = 0; // locked out
        this.boostHeat = Math.max(0, this.boostHeat - BOOST_HEAT_DOWN * dt);
        if (this.boostHeat <= BOOST_RESUME) this.overheated = false;
      } else {
        this.boostHeat = THREE.MathUtils.clamp(
          this.boostHeat + (boostAmt > 0.05 ? BOOST_HEAT_UP * boostAmt : -BOOST_HEAT_DOWN) * dt, 0, 1);
        if (this.boostHeat >= 1) { this.overheated = true; boostAmt = 0; game.audio.playAlarm(); }
      }
      useStore.getState().setBoostThermal(this.boostHeat, this.overheated);
    }
    this.boostFactor += (boostAmt - this.boostFactor) * (1 - Math.exp(-dt / 0.18));

    // 0b. Energy / momentum airspeed. Gravity trades speed for altitude: climbSin = forward.y
    //     (last frame's climb angle, +1 up / -1 down). Thrust restores the cruise target (boost
    //     raises it); gravity bleeds speed climbing and adds it diving. So you can't hang on the
    //     prop forever, and a dive pays the energy back as an overspeed rush.
    const cruiseTarget = this.V * (1 + (BOOST_MULT - 1) * this.boostFactor);
    // The energy model + mush are a HUMAN-flying mechanic: gate the player's zoom-climb, sell the
    // stall. AI wingmen fly scripted combat patterns (dive-strafe, climb-out, chase-above) that
    // assume constant-speed handling — subject them to mush and they bleed energy in every
    // climb-out, then the stall nose-drop bias overrides even their emergency GA_FLOOR pull-up and
    // they auger into the terrain. So ONLY the controlled plane runs the energy model; AI holds
    // cruise airspeed with no stall (exactly the pre-energy-model behaviour).
    let mush = 0;
    if (this.controlled) {
      const dVthrust = (cruiseTarget - this.airspeed) / THRUST_TAU;
      const dVgrav = -GRAV_FRAC * this.V * this.forward.y;
      this.airspeed = THREE.MathUtils.clamp(
        this.airspeed + (dVthrust + dVgrav) * dt,
        this.V * MIN_AIR_FRAC, cruiseTarget * OVERSPEED_FRAC);
      // Mush factor: 0 = healthy, 1 = fully stalled. Below MUSH_FRAC of cruise the elevator loses
      // bite and gravity sags the nose, so a too-steep climb noses over on its own and recovers by
      // diving. Never a hard break — partial mush is a soft sag you can fly out of.
      mush = THREE.MathUtils.clamp((this.V * MUSH_FRAC - this.airspeed) / (this.V * MUSH_FRAC), 0, 1);
    } else {
      this.airspeed = cruiseTarget; // constant-speed AI — no energy bleed, no stall into the ground
    }

    // Stall gauge + horn (controlled plane only). stallWarn ramps 0→1 as airspeed falls from
    // STALL_WARN_FRAC of cruise down to the mush floor, so the RPM needle redlines and the horn
    // starts BEFORE the nose actually sags — a warning, not a post-mortem. Beeps accelerate from
    // ~0.6s apart to near-continuous as the danger climbs.
    if (this.controlled) {
      const stallWarn = THREE.MathUtils.clamp(
        (this.V * STALL_WARN_FRAC - this.airspeed) / (this.V * (STALL_WARN_FRAC - MUSH_FRAC)), 0, 1);
      useStore.getState().setFlightGauge(this.airspeed / this.V, stallWarn);
      if (stallWarn > 0.001) {
        this.stallBeepTimer -= dt;
        if (this.stallBeepTimer <= 0) {
          game.audio.playStallBeep(stallWarn);
          this.stallBeepTimer = 0.6 - 0.46 * stallWarn; // 0.6s (mild) → 0.14s (mushing)
        }
      } else {
        this.stallBeepTimer = 0; // primed to blip immediately on the next entry into the band
      }
    }

    // 1. Pitch — body-X rotation, smoothed. Unbounded so loops work. Airspeed gates authority:
    //    when mushing, the elevator weakens (×(1-0.6·mush)) and a gravity bias sags the nose, so
    //    you must unload and dive to regain energy instead of climbing to space.
    const targetPitch = -stick.y * this.maxPitch * (1 - 0.6 * mush) - mush * this.maxPitch * 0.5;
    this.pitchRate += (targetPitch - this.pitchRate) * (1 - Math.exp(-dt / SMOOTH_TAU));
    q.multiply(_qx.setFromAxisAngle(AXIS_X, this.pitchRate * dt));

    // 2. Roll — seek the target bank angle (the plane banks and HOLDS; releasing levels it).
    _vRight.set(1, 0, 0).applyQuaternion(q);
    const bank = Math.asin(THREE.MathUtils.clamp(-_vRight.y, -1, 1)); // + = right wing down = banked right
    const targetBank = stick.x * MAX_BANK;
    const rollRate = THREE.MathUtils.clamp((targetBank - bank) * this.rollGain, -MAX_ROLL_RATE, MAX_ROLL_RATE);
    q.multiply(_qz.setFromAxisAngle(AXIS_Z, -rollRate * dt));

    // 3. Coordinated turn — world-Y yaw proportional to sin(bank). Banking turns the nose.
    const turnRate = Math.sin(bank) * this.maxTurn;
    q.premultiply(_qy.setFromAxisAngle(AXIS_Y, -turnRate * dt));
    q.normalize();

    this.curRoll = rollRate;
    this.curTurn = turnRate;

    // VIEWPOINT (speed-rush FOV) is NOT analog — it widens only on a committed, non-overheated
    // boost, so the view doesn't drift as you feather the throttle. On a thermal cutout the view
    // pulling back is part of the tell.
    const fovWant = (boostAmt > 0.5 && !this.overheated) ? 1 : 0;
    this._fovBoost += (fovWant - this._fovBoost) * (1 - Math.exp(-dt / 0.18));
    const fov = BASE_FOV + (BOOST_FOV - BASE_FOV) * this._fovBoost;
    if (this.camera && Math.abs(this.camera.fov - fov) > 0.05) { this.camera.fov = fov; this.camera.updateProjectionMatrix(); }

    // Advance along the new forward direction at the live airspeed (energy model, step 0b).
    this.forward.set(0, 0, -1).applyQuaternion(q);
    this.group.position.addScaledVector(this.forward, this.airspeed * dt);

    // Contrail — vapour streak off the tail (thicker under boost). Added to the world once.
    if (!this._trailAdded) { ctx.scene.add(this.trail.object); this._trailAdded = true; }
    _trailPt.copy(this.group.position).addScaledVector(this.forward, -3.6); _trailPt.y -= 0.2;
    this.trail.update(_trailPt);

    // Terrain kills (crash floor + mountains) are suspended for the controlled plane while the UFO
    // is abducting it — the encounter fires as you reach the mountain ring, so a peak must not
    // interrupt the whisk-away.
    const abducting = this.controlled && game.isUfoActive();

    // Soft floor so you don't dive through terrain
    if (!abducting && this.group.position.y < -450) {
      this.killFromCrash(ctx);
      return;
    }

    // Mountain collision — a solid CONE from base to summit (radius tapers with height), so you
    // can't fly UNDER the rock near the deck the way the old peak-only sphere let you.
    if (!abducting) {
      const py = this.group.position.y;
      for (const peak of game.gameScene.peaks) {
        if (py >= peak.summitY) continue; // above the tip — cleared it
        const coneR = peak.radius * (peak.summitY - Math.max(py, peak.baseY)) / (peak.summitY - peak.baseY);
        const dx = this.group.position.x - peak.pos.x;
        const dz = this.group.position.z - peak.pos.z;
        const hit = coneR + this.radius;
        if (dx * dx + dz * dz < hit * hit) {
          this.killFromCrash(ctx);
          return;
        }
      }
    }

    // Auto-fire with distance-scaled cone + target-leading.
    // For multi-part bosses (Sky Carrier, Juggernaut, etc.) we scan against each
    // sub-part separately — otherwise the centroid (the boss's invincible hull) gets
    // priority and bullets fly into nothing.
    this.fireCooldown -= dt;
    const enemies = game.findEnemies();
    // Evaluate every candidate aim point in place via scratch vectors — building a cloned-Vector3
    // array per enemy per frame was steady GC churn. The winning point is kept in _ppAimBest.
    // (Mutable record rather than locals: TS can't track closure mutations of narrowed lets.)
    const best = { inCone: false, e: null as Entity | null, pos: null as THREE.Vector3 | null, d: Infinity };
    const evalPoint = (e: Entity, targetPos: THREE.Vector3) => {
      const toE = _ppToE.copy(targetPos).sub(this.group.position);
      const d = toE.length();
      if (d > FIRE_RANGE) return;
      toE.normalize();
      const dot = toE.dot(this.forward);
      const angle = Math.acos(THREE.MathUtils.clamp(dot, -1, 1));
      const t = THREE.MathUtils.clamp((d - FIRE_NEAR) / (FIRE_RANGE - FIRE_NEAR), 0, 1);
      const coneDeg = FIRE_CONE_DEG_NEAR + (FIRE_CONE_DEG_FAR - FIRE_CONE_DEG_NEAR) * t;
      if (angle <= THREE.MathUtils.degToRad(coneDeg)) {
        best.inCone = true;
        if (d < best.d) {
          best.d = d;
          best.e = e;
          best.pos = _ppAimBest.copy(targetPos);
        }
      }
    };
    for (const e of enemies) {
      if (!e.alive()) continue;
      // Decide what to aim at: each hit-part on multi-part bosses, else the centroid.
      if (e.hitParts && e.hitParts.length > 0) {
        for (const part of e.hitParts) {
          part.object.getWorldPosition(_ppWp);
          evalPoint(e, _ppWp);
        }
      } else {
        evalPoint(e, e.position);
      }
    }
    const targetInCone = best.inCone;
    const aimEnemy = best.e;
    const aimPos = best.pos;
    // Manual fire when you're crewing it (RT / touch FIRE) — auto-fire only for AI wingmen. The
    // aim-assist still leads the nearest in-cone target if there is one; otherwise rounds go straight
    // ahead, so holding fire with no lock still shoots.
    const firing = this.controlled ? game.fireHeld() : targetInCone;
    // Gremlins random event: guns cough — most rounds misfire while jammed.
    const jam = this.jammed && Math.random() < 0.6;
    if (firing && !jam && this.fireCooldown <= 0) {
      this.spawnBullet(ctx, aimEnemy, aimPos);
      // ACE BONUS: the unit you personally fly reloads faster. AI wingmen are unchanged —
      // your presence is the upgrade, the help isn't handicapped. fireMult<1 = Munitions overcharge.
      this.fireCooldown = (this.controlled ? this.fireInterval * ACE_FIRE_MULT : this.fireInterval) * this.fireMult;
    }
    // Report the current target for the map's line: what we're firing at if anything, else
    // (for AI-flown units) the enemy the autopilot is pursuing toward.
    this.engageTargetId = aimEnemy ? aimEnemy.id : (aiDriven ? this.aiPursuitId : 0);
    // Only the controlled plane drives the HUD's firing indicator — allied wingmen fire
    // on their own but mustn't clobber it.
    if (this.controlled) useStore.getState().setFiring(firing);

    // Regen
    if (now - this.lastDamageAt > REGEN_DELAY && this.hp < this.maxHpStat) {
      const regen = this.regenRate * dt;
      this.hp = Math.min(this.maxHpStat, this.hp + regen);
      // Likewise, only sync the HUD HP bar for the unit the player is actually flying.
      if (this.controlled) useStore.getState().setHp(Math.floor(this.hp));
    }
  }

  private spawnBullet(ctx: EntityCtx, aimAt: Entity | null, aimPos: THREE.Vector3 | null) {
    const BULLET_SPEED = 700; // faster bullets = less lead required, fewer perpendicular-target misses
    // Twin Spandau: same forward spawn distance as before (well ahead of the cowl, so the round is
    // never a big disc in the cockpit), just offset left/right along the plane's own axis so rounds
    // alternate between the barrels and line up with the guns.
    this._gunSide = -this._gunSide;
    const right = this._muzzleLocal.set(1, 0, 0).applyQuaternion(this.group.quaternion);
    const muzzle = this.group.position.clone()
      .addScaledVector(this.forward, 4)
      .addScaledVector(right, this._gunSide * 0.3);

    // Default aim: straight forward (plus plane velocity added for a ballistic inheritance).
    let aimDir = this.forward.clone();

    if (aimAt) {
      // Aim-assist: lead the target via the shared intercept solver. Use the explicit `aimPos`
      // (the nearest hit-part for multi-part bosses) so we lead toward something hittable, not
      // the invincible centroid. Falls back to the target's current position when no intercept
      // lands within 3s.
      const target = aimPos ?? aimAt.position;
      aimDir = interceptPoint(muzzle, target, entityVelocity(aimAt), BULLET_SPEED, 3).sub(muzzle).normalize();
    }

    const vel = aimDir.clone().multiplyScalar(BULLET_SPEED);
    // Inherit a small fraction of plane velocity so tracers look right when you turn.
    vel.addScaledVector(this.forward, this.V * 0.3);
    const b = new Bullet(muzzle, vel, this.id, 'player');
    // ACE BONUS: rounds from the unit you fly hit harder than an AI wingman's.
    b.damageAmount = this.controlled ? ACE_BULLET_DAMAGE : 1;
    b.friendlyFire = this.controlled; // your own fire can hit friendlies + villages
    ctx.game.add(b);
  }

  damage(amount: number, ctx: EntityCtx) {
    this.hp -= amount;
    this.lastDamageAt = ctx.now;
    // Only the controlled unit drives the HUD HP bar + hit flash.
    if (this.controlled) {
      useStore.getState().takeDamage();
      useStore.getState().setHp(Math.max(0, Math.floor(this.hp)));
    }
    if (this.hp <= 0) this.onDestroyed(ctx);
  }

  private killFromCrash(ctx: EntityCtx) {
    this.beginTumble();
    this.tumbleVel.multiplyScalar(1.4); // mountain/ground crash tumbles harder
    this.onDestroyed(ctx);
  }

  /** Unified destruction. Controlled unit: hand off to another ally (commander jump) or,
   *  if none remain, play the cinematic game-over. Allied AI fighter: just blow up. */
  private onDestroyed(ctx: EntityCtx) {
    if (this.controlled) {
      const handed = ctx.game.onControlledLost(this);
      if (handed) {
        spawnWreckage(ctx, this.group.position.clone(), 'plane', this.livery,
          { baseVel: this.forward.clone().multiplyScalar(this.V), count: 7, boom: 1.0, impact: { radius: 38, damage: 5 } });
        this.release();
        this.dead = true;
        return;
      }
      // No allies left to jump to → this is the real game over: cinematic crash.
      this.beginTumble();
      useStore.getState().setHp(0);
      useStore.getState().setPhase('gameover');
      this.triggerDeathEffects(ctx);
      return;
    }
    // Allied AI fighter shot down — burst into tumbling debris that craters where it lands.
    spawnWreckage(ctx, this.group.position.clone(), 'plane', this.livery,
      { baseVel: this.forward.clone().multiplyScalar(this.V), count: 6, boom: 1.0, impact: { radius: 38, damage: 5 } });
    this.dead = true;
  }

  private beginTumble() {
    this.tumbleVel.copy(this.forward).multiplyScalar(this.V * 0.6);
    this.tumbleRot.set((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2);
  }

  private triggerDeathEffects(ctx: EntityCtx) {
    const store = useStore.getState();
    // Clear boost so the wide FOV doesn't stick into the crash cam, and reset the burner thermal
    // so the HUD ring isn't stuck hot on the score screen / next life.
    store.setBoost(false);
    store.setBoostThermal(0, false);
    store.setFlightGauge(1, 0); // settle the RPM needle + silence the stall gauge on the crash cam
    this.boostFactor = 0;
    this._fovBoost = 0;
    this.boostHeat = 0;
    this.overheated = false;
    this.stallBeepTimer = 0;
    if (this.camera) { this.camera.fov = BASE_FOV; this.camera.updateProjectionMatrix(); }
    store.setLossReason('shotdown');
    store.beginDeath(ctx.now);
    store.takeDamage(); // drive a red hit-flash on the HUD
    // Swap to the full exterior Fokker model; the cockpit was only for first-person.
    this.cockpit.visible = false;
    this.fullModel.visible = true;
    // Cockpit fireball — big initial boom, front-loaded fire.
    new Explosion(this.group.position.clone(), 48, 1.0, 'fire').addTo(ctx);
    new Explosion(this.group.position.clone(), 22, 0.6, 'fire').addTo(ctx);
    // Initial smoke cloud
    new Explosion(this.group.position.clone(), 30, 3.0, 'smoke')
      .setDrift(new THREE.Vector3(0, 10, 0))
      .addTo(ctx);
    // Audio: booooom
    ctx.game.audio.playBoom(1.2);
    // Full-screen red flash quad parented briefly to the camera.
    this.spawnDeathFlash(ctx);
  }

  private spawnDeathFlash(ctx: EntityCtx) {
    const mat = new THREE.MeshBasicMaterial({ color: 0xff2020, transparent: true, opacity: 0.75, depthWrite: false, depthTest: false });
    const geom = new THREE.PlaneGeometry(2, 2);
    const quad = new THREE.Mesh(geom, mat);
    quad.renderOrder = 999;
    quad.frustumCulled = false;
    const cam = ctx.game.camera;
    cam.add(quad);
    quad.position.set(0, 0, -0.6);
    const duration = 0.7;
    let t = 0;
    const tick = () => {
      t += 1 / 60;
      const k = Math.max(0, 1 - t / duration);
      mat.opacity = 0.75 * k;
      if (k <= 0) {
        quad.removeFromParent();
        mat.dispose();
        geom.dispose();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private updateCrashSequence(ctx: EntityCtx) {
    const { dt } = ctx;
    const cam = this.camera;
    if (!cam) return; // only the possessed-at-death unit runs the cinematic
    // Advance wreckage physics — tumble + gravity.
    this.group.position.addScaledVector(this.tumbleVel, dt);
    this.tumbleVel.y -= 26 * dt;
    this.group.rotation.x += this.tumbleRot.x * dt;
    this.group.rotation.y += this.tumbleRot.y * dt;
    this.group.rotation.z += this.tumbleRot.z * dt;

    // Continuous fire + smoke trail while falling.
    this.crashSmokeTimer += dt;
    if (this.crashSmokeTimer >= 0.05) {
      this.crashSmokeTimer = 0;
      new Explosion(this.group.position.clone(), 20, 2.8, 'smoke')
        .setDrift(new THREE.Vector3(0, 6, 0))
        .addTo(ctx);
      if (Math.random() < 0.5) {
        new Explosion(this.group.position.clone(), 8, 0.4, 'fire').addTo(ctx);
      }
    }

    // Detach camera from the plane so the player sees the crash from outside.
    if (!this.cameraDetached) {
      this.cameraDetached = true;
      // Parent camera to the scene root (its current world transform is preserved when
      // we pick a new parent via scene.attach).
      ctx.scene.attach(cam);
      // Initialize orbit offset based on the plane's current forward.
      this.deathOrbitAngle = Math.atan2(this.forward.x, this.forward.z);
    }

    // Orbit the camera around the wreckage, slowly closing in. Clamp above the local
    // terrain surface so the camera doesn't dip below the ground.
    this.deathOrbitAngle += 0.35 * dt;
    const radius = 24 + Math.max(0, 14 - ctx.now % 10);
    const wreck = this.group.position;
    const groundY = ctx.game.gameScene.heightAt(wreck.x, wreck.z);
    const camY = Math.max(wreck.y + this.deathOrbitHeight, groundY + 30);
    cam.position.set(
      wreck.x + Math.cos(this.deathOrbitAngle) * radius,
      camY,
      wreck.z + Math.sin(this.deathOrbitAngle) * radius,
    );
    cam.lookAt(wreck.x, wreck.y, wreck.z);

    // Ground impact: the airframe disintegrates into burning, tumbling wreckage that the
    // orbiting death-cam watches scatter and cook off.
    if (!this.impactLogged && this.group.position.y <= groundY) {
      this.impactLogged = true;
      this.group.position.y = groundY;
      this.tumbleVel.set(0, 0, 0);
      this.tumbleRot.set(0, 0, 0);
      useStore.getState().markDeathImpact(ctx.now);
      // Hide the intact airframe and shatter it — the camera keeps orbiting this point.
      this.cockpit.visible = false;
      this.fullModel.visible = false;
      spawnWreckage(ctx, this.group.position.clone(), 'plane', this.livery,
        { baseVel: this.forward.clone().multiplyScalar(this.V * 0.5), count: 10, boom: 1.6 });
      this.spawnDeathFlash(ctx);
      // Your own wreck levels the trees/buildings it plows into and hurts anything beneath it.
      ctx.game.crashImpact(ctx, this.group.position.clone().setY(groundY), 44, 6, this);
    }

    // After impact, keep emitting slow smoke from the crash site so it's a lingering plume.
    if (this.impactLogged && Math.random() < 0.15) {
      new Explosion(
        this.group.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 8, 2, (Math.random() - 0.5) * 8)),
        30,
        4,
        'smoke',
      ).setDrift(new THREE.Vector3(0, 9, 0)).addTo(ctx);
    }
  }
}

/** Approximate an enemy entity's current world velocity. Used for bullet lead-targeting.
 *  Reads the entity's own `speed` field when available so lead is correct for tanks
 *  (slow), scouts (fast), bombers (slow), aces (medium) — instead of assuming 62 u/s
 *  for everyone non-zeppelin (which over-leads slow targets and under-leads fast ones). */

// Autopilot: proportional controller that points the plane at the nearest enemy.
// Stick magnitude scales with the MISS ANGLE (angle between forward and target-direction)
// so we don't full-commit a turn when we're already almost aligned — that's what caused
// the loop-and-loop overshoot. When close to aligned, stick is nearly zero and the plane
// just flies toward the target.
const _ap_toTarget = new THREE.Vector3();
const _ap_invQ = new THREE.Quaternion();
const _ap_local = new THREE.Vector3();
const _ap_loiter = new THREE.Vector3();
const _ga = new THREE.Vector3();
const DEFEND_R2 = 1400 * 1400; // enemies within this of a village count as attacking it
// Ground-attack run profile (world Y; terrain ≈ -500): approach high, roll into a dive on
// the tank, pull up before the deck, climb out, come around. Tuned so AI fighters strafe
// armour without auguring in.
const GA_ATTACK_ALT = 190;   // approach/setup altitude
const GA_CLIMB_TO = 150;     // climb back to this between passes
const GA_PULLUP_Y = -300;    // start the pull-up once the dive reaches here (~200 over ground)
const GA_ROLLIN = 440;       // horizontal distance at which the dive begins
const GA_FLOOR = -350;       // hard emergency pull-up floor (kill plane sits at -450)
function autopilotStick(player: PlayerPlane, game: Game): { x: number; y: number; active: boolean; source: 'ai' } {
  // Choose what to fly at based on the unit's standing order (set from the tactical map).
  // Also record the enemy being pursued (`pursued`) so the map can draw a line to it even
  // before it's close enough to fire on — that's the unit's live "target" indicator.
  let targetPos: THREE.Vector3 | null = null;
  let pursued: Entity | null = null;
  const order = player.order;

  // Nearest live enemy (air OR ground) within `r2` of a point. Altitude counts in the
  // distance, so a fighter naturally prefers a nearby aircraft over a tank far below —
  // ground armour is engaged when it's genuinely the closest threat, or when ordered.
  const nearestThreat = (px: number, py: number, pz: number, r2: number): Entity | null => {
    let best: Entity | null = null;
    for (const e of game.findEnemies()) {
      if (!e.alive()) continue;
      const dx = e.position.x - px, dy = e.position.y - py, dz = e.position.z - pz;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < r2) { r2 = d2; best = e; }
    }
    return best;
  };

  // Ground-attack run: steering point for strafing a tank, advancing this fighter's dive
  // state machine. Approach overhead → roll into a dive on the tank → pull up at the floor
  // and climb out → come around for another pass.
  const groundStrafe = (t: Entity): THREE.Vector3 => {
    const p = player.position;
    const horiz = Math.hypot(t.position.x - p.x, t.position.z - p.z);
    if (player.gaPhase === 'climb') {
      if (p.y >= GA_CLIMB_TO) player.gaPhase = 'approach';
    } else if (player.gaPhase === 'dive') {
      if (p.y <= GA_PULLUP_Y || horiz < 70) player.gaPhase = 'climb';
    } else { // approach
      if (horiz < GA_ROLLIN && p.y > GA_PULLUP_Y + 120) player.gaPhase = 'dive';
    }
    if (player.gaPhase === 'dive') return _ga.copy(t.position);                              // nose onto the tank
    if (player.gaPhase === 'climb') return _ga.set(p.x + player.forward.x * 500, GA_CLIMB_TO + 90, p.z + player.forward.z * 500);
    return _ga.set(t.position.x, GA_ATTACK_ALT, t.position.z);                               // set up overhead
  };

  // Resolve a chosen enemy into a steering point — strafing run for tanks, direct for air.
  const aimAt = (e: Entity): THREE.Vector3 => {
    if (e.kind === 'tank') return groundStrafe(e);
    player.gaPhase = 'approach'; // reset run state whenever we're not on a ground target
    return e.position;
  };

  if (order.type === 'attack') {
    const t = game.enemyById(order.targetId);
    if (t) { targetPos = aimAt(t); pursued = t; }
    else player.order = { type: 'patrol' };          // assigned target is gone → free up
  } else if (order.type === 'defend') {
    const v = game.villageCenter(order.villageIndex);
    if (v) {
      const near = nearestThreat(v.x, v.y, v.z, DEFEND_R2);
      if (near) { targetPos = aimAt(near); pursued = near; }
      else { player.gaPhase = 'approach'; targetPos = _ap_loiter.set(v.x, 160, v.z); }  // no threat → loiter
    } else {
      player.order = { type: 'patrol' };              // village lost → free up
    }
  } else if (order.type === 'escort') {
    const u = game.friendlyById(order.unitId);
    if (u && u !== player) {
      const near = nearestThreat(u.position.x, u.position.y, u.position.z, DEFEND_R2);
      if (near) { targetPos = aimAt(near); pursued = near; }
      else { player.gaPhase = 'approach'; targetPos = _ap_loiter.set(u.position.x, Math.max(140, u.position.y + 30), u.position.z); }
    } else {
      player.order = { type: 'patrol' };              // escortee gone → free up
    }
  }

  if (!targetPos) {
    // Patrol (default / fallback): hunt the nearest threat anywhere.
    const nearest = nearestThreat(player.position.x, player.position.y, player.position.z, Infinity);
    if (nearest) { targetPos = aimAt(nearest); pursued = nearest; }
    else {
      // No threats anywhere: loiter back over the centre of the map at a safe altitude — steer
      // for a point drawn toward the origin so idle wingmen orbit the battlefield instead of
      // flying dead straight into the mountain ring (or, via the old inverted pitch term here,
      // nosing into the ground).
      player.aiPursuitId = 0; player.gaPhase = 'approach';
      const pp = player.position;
      targetPos = _ap_loiter.set(pp.x * 0.4, 150, pp.z * 0.4);
    }
  }

  // Hard boundary: an AI plane must never press out into the mountain ring (peaks reach in to
  // ~4800). Past this radius, abandon the chase and steer for the centre so it can't crash the way
  // the player deliberately can.
  if (Math.hypot(player.position.x, player.position.z) > 4300) {
    targetPos = _ap_loiter.set(0, Math.max(150, player.position.y), 0);
    pursued = null; player.aiPursuitId = 0;
  }

  // Surface the pursued enemy for the tactical map's target line.
  player.aiPursuitId = pursued ? pursued.id : 0;

  _ap_toTarget.copy(targetPos).sub(player.position).normalize();
  _ap_invQ.copy(player.group.quaternion).invert();
  _ap_local.copy(_ap_toTarget).applyQuaternion(_ap_invQ);

  // local.x = +right, local.y = +up, local.z = +behind. Forward is -z.
  const ahead = -_ap_local.z; // positive = in front
  const lateralMag = Math.hypot(_ap_local.x, _ap_local.y); // magnitude off-axis

  let sx = 0, sy = 0;
  if (ahead > 0) {
    // Target is ahead — proportional stick that tapers to zero as we align.
    // miss angle ≈ asin(lateralMag). We scale stick by angle / threshold, full stick at ~45°.
    const missAngle = Math.asin(Math.min(1, lateralMag));
    const mag = Math.min(1, missAngle / (Math.PI / 4));
    if (lateralMag > 1e-4) {
      sx = (_ap_local.x / lateralMag) * mag;
      sy = -(_ap_local.y / lateralMag) * mag;
    }
  } else {
    // Target is BEHIND — hard commit, biased to the side they're on.
    const side = Math.sign(_ap_local.x) || 1;
    sx = side * 0.85;
    sy = THREE.MathUtils.clamp(-_ap_local.y * 0.6, -0.5, 0.5);
  }

  // Strafing pull-up: in a ground-attack dive the fighter is allowed all the way down to
  // GA_PULLUP_Y (the run's own logic then climbs out). GA_FLOOR is the hard emergency floor
  // — below it, override to a strong climb so a fighter never auger-ins, ground attack or not.
  if (player.position.y < GA_FLOOR) sy = -0.95;
  else if (player.gaPhase !== 'dive' && player.position.y < GA_PULLUP_Y) sy = Math.min(sy, -0.6);

  return { x: sx, y: sy, active: true, source: 'ai' };
}

// Detailed Fokker Dr.I triplane — Red Baron livery, airfoil-shaped wings, Oberursel
// rotary engine cylinders, full pilot figure, and balanced proportions per historical
// references. Nose points along local -Z.
function makeFokkerTriplane(): THREE.Group {
  const g = new THREE.Group();

  const red = new THREE.MeshStandardMaterial({ color: 0xa02424, roughness: 0.72 });
  const redDeep = new THREE.MeshStandardMaterial({ color: 0x7a1616, roughness: 0.85 });
  const redShade = new THREE.MeshStandardMaterial({ color: 0x8b1e1e, roughness: 0.8 });
  const fabric = new THREE.MeshStandardMaterial({ color: 0xb43030, roughness: 0.95 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x3a3a38, roughness: 0.35, metalness: 0.7 });
  const blackMetal = new THREE.MeshStandardMaterial({ color: 0x161614, roughness: 0.55, metalness: 0.4 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.95 });
  const woodLight = new THREE.MeshStandardMaterial({ color: 0x6a4a2d, roughness: 0.9 });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x121212, roughness: 0.95 });
  // Decals get polygonOffset so they don't z-fight against wing/fuselage surfaces.
  const black = new THREE.MeshBasicMaterial({ color: 0x0b0b0b, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const white = new THREE.MeshBasicMaterial({ color: 0xe6e6e6, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const leatherMat = new THREE.MeshStandardMaterial({ color: 0x5a4838, roughness: 0.9 });
  const goggleMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.2, metalness: 0.6 });
  const scarfMat = new THREE.MeshStandardMaterial({ color: 0xe8e1d4, roughness: 0.9 });

  // --- Fuselage: custom tapered hull with an octagonal cross-section per station ---
  // Fokker Dr.I hull was steel-tube + fabric: flat sides, rounded top, tapered aft.
  // We build a BufferGeometry with several stations along Z, each an 8-gon cross-section,
  // stitched with quads — so the hull actually changes shape along the plane's length.
  const hull = makeFokkerHull(red, redShade, redDeep);
  g.add(hull);
  // Belly longitudinal stringers (dark strips for fabric detail)
  for (let i = -1; i <= 1; i++) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 4.5, 2, 2, 24), redDeep);
    s.position.set(i * 0.3, -0.56, 0.45);
    g.add(s);
  }

  // --- Nose cowling (Oberursel rotary) ---
  const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.7, 0.7, 80, 4), metal);
  cowl.rotation.x = Math.PI / 2;
  cowl.position.set(0, 0, -2.2);
  g.add(cowl);
  // Cowling lip — horseshoe cutout at the bottom (characteristic of rotary engines for exhaust/oil)
  const cowlShadow = new THREE.Mesh(
    new THREE.CylinderGeometry(0.5, 0.5, 0.74, 80, 4, false, Math.PI, Math.PI),
    new THREE.MeshStandardMaterial({ color: 0x0c0c0c, roughness: 1 }),
  );
  cowlShadow.rotation.x = Math.PI / 2;
  cowlShadow.position.set(0, 0, -2.18);
  g.add(cowlShadow);
  // Rotary engine cylinders visible through the cowl cutout — 9 short cylinders in a ring.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const cyl = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.085, 0.28, 24, 2), blackMetal);
    cyl.position.set(Math.cos(a) * 0.4, Math.sin(a) * 0.4, -2.45);
    cyl.rotation.x = Math.PI / 2;
    g.add(cyl);
    // Cooling fins hint (thin disc at the top of each cylinder)
    const fin = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.02, 20, 1), metal);
    fin.position.set(Math.cos(a) * 0.4, Math.sin(a) * 0.4, -2.3);
    fin.rotation.x = Math.PI / 2;
    g.add(fin);
  }

  // --- Propeller: airfoil blades on a metal hub with a spinner ---
  const prop = new THREE.Group();
  const bladeGeom = makePropBladeGeomF(2.5, 0.22);
  const b1 = new THREE.Mesh(bladeGeom, wood);
  const b2 = new THREE.Mesh(bladeGeom, wood);
  b2.rotation.z = Math.PI / 2;
  prop.add(b1);
  prop.add(b2);
  const hub = new THREE.Mesh(new THREE.SphereGeometry(0.2, 64, 40), metal);
  prop.add(hub);
  // Spinner cap
  const spinner = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.34, 48, 4), redDeep);
  spinner.rotation.x = -Math.PI / 2;
  spinner.position.set(0, 0, -0.25);
  prop.add(spinner);
  prop.position.set(0, 0, -2.6);
  g.add(prop);
  g.userData.prop = prop;

  // --- Cockpit area ---
  // Dark inset (seat well)
  const pit = new THREE.Mesh(
    new THREE.BoxGeometry(0.72, 0.32, 1.05, 8, 4, 10),
    new THREE.MeshStandardMaterial({ color: 0x120c08, roughness: 1 }),
  );
  pit.position.set(0, 0.63, 0.3);
  g.add(pit);
  // Padded leather rim around the cockpit opening
  const rimTop = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.06, 20, 60), leatherMat);
  rimTop.rotation.x = Math.PI / 2;
  rimTop.position.set(0, 0.78, 0.3);
  rimTop.scale.set(0.75, 1.1, 1);
  g.add(rimTop);

  // --- Three wings, airfoil-extruded ---
  const wingSpec: [number, number, number, number][] = [
    // [span, chord, y_offset, z_offset]
    [7.2, 1.15, 2.0, -0.8],   // upper
    [6.2, 1.15, 0.85, -0.4],  // middle
    [5.7, 1.15, -0.5, 0.0],   // lower
  ];
  const wingThick = 0.18;
  for (const [span, chord, y, z] of wingSpec) {
    const w = makeFokkerWing(span, chord, wingThick, fabric, redDeep);
    w.position.set(0, y, z);
    g.add(w);
  }

  // --- Iron crosses (Balkenkreuz) ---
  const drawCross = (parent: THREE.Object3D, x: number, y: number, z: number, rotEuler: THREE.Euler, size = 0.8) => {
    const sq = new THREE.Mesh(new THREE.PlaneGeometry(size, size, 1, 1), white);
    sq.position.set(x, y, z);
    sq.rotation.copy(rotEuler);
    parent.add(sq);
    const bar = size * 0.28;
    const len = size * 0.92;
    const cv = new THREE.Mesh(new THREE.PlaneGeometry(bar, len, 1, 1), black);
    cv.position.set(x, y, z);
    cv.rotation.copy(rotEuler);
    parent.add(cv);
    const ch = new THREE.Mesh(new THREE.PlaneGeometry(len, bar, 1, 1), black);
    ch.position.set(x, y, z);
    ch.rotation.copy(rotEuler);
    parent.add(ch);
    const n = new THREE.Vector3(0, 0.001, 0).applyEuler(rotEuler);
    cv.position.add(n);
    ch.position.add(n);
  };
  const topEuler = new THREE.Euler(-Math.PI / 2, 0, 0);
  const botEuler = new THREE.Euler(Math.PI / 2, 0, 0);
  const leftEuler = new THREE.Euler(0, -Math.PI / 2, 0);
  const rightEuler = new THREE.Euler(0, Math.PI / 2, 0);
  drawCross(g, 2.6, 2.0 + 0.12, -0.8, topEuler);
  drawCross(g, -2.6, 2.0 + 0.12, -0.8, topEuler);
  drawCross(g, 2.1, -0.5 - 0.12, 0.0, botEuler);
  drawCross(g, -2.1, -0.5 - 0.12, 0.0, botEuler);
  drawCross(g, 0.57, 0.0, 1.0, rightEuler, 0.7);
  drawCross(g, -0.57, 0.0, 1.0, leftEuler, 0.7);

  // --- Struts: short I-struts between wings, single kingpost pair to upper wing ---
  for (const dx of [-0.45, 0.45]) {
    const s1 = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 1.25, 32, 2), woodLight);
    s1.position.set(dx, 1.42, -0.6);
    g.add(s1);
    const s2 = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 1.4, 32, 2), woodLight);
    s2.position.set(dx, 0.17, -0.2);
    g.add(s2);
  }
  // Characteristic outboard "N-struts" on the Dr.I between upper and middle wing tips.
  for (const dx of [-2.9, 2.9]) {
    const n1 = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 1.25, 24, 2), woodLight);
    n1.position.set(dx, 1.42, -0.6);
    g.add(n1);
    const n2 = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.55, 20, 2), woodLight);
    n2.position.set(dx, 1.42, -0.6);
    n2.rotation.z = dx > 0 ? 0.15 : -0.15;
    n2.rotation.x = 0.1;
    g.add(n2);
  }

  // --- Tail surfaces ---
  // Horizontal stab + elevator, rounded with segment count bumps
  const hstab = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.12, 1.0, 28, 2, 8), fabric);
  hstab.position.set(0, 0.0, 3.2);
  g.add(hstab);
  const elev = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.14, 0.35, 28, 2, 4), redDeep);
  elev.position.set(0, 0.0, 3.65);
  g.add(elev);
  // Fin + comma-shaped rudder
  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.9, 1.1, 4, 12, 8), fabric);
  fin.position.set(0, 0.55, 3.25);
  g.add(fin);
  const rudder = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.0, 0.7, 4, 12, 8), redDeep);
  rudder.position.set(0, 0.55, 3.85);
  g.add(rudder);
  drawCross(g, 0.09, 0.55, 3.85, rightEuler, 0.5);
  drawCross(g, -0.09, 0.55, 3.85, leftEuler, 0.5);
  // Tail skid
  const skid = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.6, 24, 2), wood);
  skid.position.set(0, -0.4, 3.0);
  skid.rotation.x = 0.4;
  g.add(skid);

  // --- Landing gear ---
  const axleLen = 2.1;
  const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, axleLen, 48, 2), wood);
  axle.rotation.z = Math.PI / 2;
  axle.position.set(0, -1.1, -0.4);
  g.add(axle);
  // Airfoil-shaped wing between landing gear legs (Dr.I signature)
  const gearWing = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.12, 0.55, 24, 2, 4), redDeep);
  gearWing.position.set(0, -1.1, -0.4);
  g.add(gearWing);
  for (const dx of [-axleLen / 2, axleLen / 2]) {
    // Hub detail + rubber wheel
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 0.18, 80, 2), rubber);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(dx, -1.1, -0.4);
    g.add(wheel);
    const hubC = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.2, 32, 2), metal);
    hubC.rotation.z = Math.PI / 2;
    hubC.position.set(dx, -1.1, -0.4);
    g.add(hubC);
    for (const dz of [-0.35, 0.35]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.9, 40, 2), wood);
      leg.position.set(dx * 0.6, -0.72, -0.4 + dz * 0.2);
      leg.rotation.z = dx > 0 ? 0.35 : -0.35;
      leg.rotation.x = dz > 0 ? 0.2 : -0.2;
      g.add(leg);
    }
  }

  // --- Full pilot figure ---
  // Torso
  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.27, 0.45, 24, 4), leatherMat);
  torso.position.set(0, 0.62, 0.45);
  g.add(torso);
  // Shoulders
  const shoulders = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.18, 0.36, 8, 3, 6), leatherMat);
  shoulders.position.set(0, 0.8, 0.45);
  g.add(shoulders);
  // Head with helmet
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 64, 40), leatherMat);
  head.position.set(0, 0.98, 0.45);
  g.add(head);
  // Goggles — dark band with two lens discs
  const goggleBand = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.06, 64, 2), goggleMat);
  goggleBand.rotation.z = Math.PI / 2;
  goggleBand.position.set(0, 0.99, 0.45);
  g.add(goggleBand);
  for (const dx of [-0.07, 0.07]) {
    const lens = new THREE.Mesh(new THREE.CircleGeometry(0.06, 48), new THREE.MeshStandardMaterial({ color: 0x0a1018, roughness: 0.2, metalness: 0.8 }));
    lens.position.set(dx, 1.0, 0.28);
    lens.rotation.y = Math.PI;
    g.add(lens);
  }
  // White scarf around the neck
  const scarf = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.06, 16, 48), scarfMat);
  scarf.position.set(0, 0.84, 0.45);
  scarf.rotation.x = Math.PI / 2;
  g.add(scarf);
  // Scarf tail trailing behind
  const scarfTail = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.5), scarfMat);
  scarfTail.position.set(0.14, 0.78, 0.9);
  scarfTail.rotation.y = 0.3;
  g.add(scarfTail);

  // --- Twin Spandau MGs ---
  for (const dx of [-0.17, 0.17]) {
    const gun = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 1.0, 48, 2), metal);
    gun.rotation.x = Math.PI / 2;
    gun.position.set(dx, 0.78, -1.6);
    g.add(gun);
    // Gun breech (rectangular block behind the barrel)
    const breech = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.22, 4, 4, 4), blackMetal);
    breech.position.set(dx, 0.78, -1.0);
    g.add(breech);
  }

  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}

/** Tapered Fokker Dr.I fuselage — 6-gon cross-section stations stitched along Z.
 *  The section is slab-sided (vertical side walls) with a rounded top, tapering to a point at the tail
 *  and to a smaller drum at the nose to meet the cowling. */
function makeFokkerHull(sideMat: THREE.Material, topMat: THREE.Material, bellyMat: THREE.Material): THREE.Group {
  const grp = new THREE.Group();
  // Stations along the fuselage (z, halfWidth, halfHeight, topSkew)
  type St = { z: number; w: number; h: number; topCurve: number };
  const stations: St[] = [
    { z: -2.20, w: 0.45, h: 0.50, topCurve: 0.50 },
    { z: -1.30, w: 0.55, h: 0.62, topCurve: 0.55 },
    { z: -0.30, w: 0.57, h: 0.65, topCurve: 0.60 },
    { z:  0.30, w: 0.55, h: 0.62, topCurve: 0.58 }, // pilot
    { z:  1.00, w: 0.52, h: 0.60, topCurve: 0.55 },
    { z:  1.90, w: 0.42, h: 0.50, topCurve: 0.45 },
    { z:  2.70, w: 0.26, h: 0.34, topCurve: 0.32 },
    { z:  3.40, w: 0.08, h: 0.12, topCurve: 0.11 },
  ];
  // 8-vertex ring per station (CCW viewed from +Z):
  //  0: bottom-right, 1: mid-right (slab), 2: upper-right-shoulder, 3: top-right,
  //  4: top-left, 5: upper-left-shoulder, 6: mid-left, 7: bottom-left
  function ring(s: St): THREE.Vector2[] {
    const w = s.w, h = s.h, c = s.topCurve;
    return [
      new THREE.Vector2(w, -h),           // 0 bottom-right
      new THREE.Vector2(w, h * 0.35),     // 1 mid-right (slab)
      new THREE.Vector2(w * 0.85, c),     // 2 shoulder right
      new THREE.Vector2(w * 0.35, h),     // 3 top right
      new THREE.Vector2(-w * 0.35, h),    // 4 top left
      new THREE.Vector2(-w * 0.85, c),    // 5 shoulder left
      new THREE.Vector2(-w, h * 0.35),    // 6 mid-left (slab)
      new THREE.Vector2(-w, -h),          // 7 bottom-left
    ];
  }
  const N = 8;

  // Build geometry: three material groups so belly / sides / top get distinct shades.
  const positions: number[] = [];
  const indices: number[] = [];
  // Per-quad classification — we track which ring edges are belly/side/top for grouping.
  const groupBelly: number[] = [];
  const groupSide: number[] = [];
  const groupTop: number[] = [];

  for (const s of stations) {
    const r = ring(s);
    for (const p of r) positions.push(p.x, p.y, s.z);
  }

  // Quads between consecutive stations. For each ring edge j→(j+1)%N.
  // Classify edges: 0 (0→1) right slab = side, 1 (1→2) upper slab = side,
  // 2 (2→3) top-right cap = top, 3 (3→4) top = top, 4 (4→5) top-left cap = top,
  // 5 (5→6) left slab = side, 6 (6→7) left slab = side, 7 (7→0) belly = belly.
  const edgeKind: ('side' | 'top' | 'belly')[] = [
    'side', 'side', 'top', 'top', 'top', 'side', 'side', 'belly',
  ];
  for (let i = 0; i < stations.length - 1; i++) {
    for (let j = 0; j < N; j++) {
      const a = i * N + j;
      const b = i * N + ((j + 1) % N);
      const c = (i + 1) * N + ((j + 1) % N);
      const d = (i + 1) * N + j;
      // Winding a,b,c and a,c,d — outward normals given our CCW ring when viewed from +Z,
      // and station order +Z advancing, faces will point outward.
      const base = indices.length;
      indices.push(a, b, c, a, c, d);
      const kind = edgeKind[j];
      for (let k = 0; k < 6; k++) {
        if (kind === 'belly') groupBelly.push(base + k);
        else if (kind === 'top') groupTop.push(base + k);
        else groupSide.push(base + k);
      }
    }
  }

  // End caps: nose (front) and tail (back) — simple fan triangulation.
  const frontCenterIdx = positions.length / 3;
  positions.push(0, 0, stations[0].z - 0.02);
  for (let j = 0; j < N; j++) {
    const a = 0 * N + j;
    const b = 0 * N + ((j + 1) % N);
    const base = indices.length;
    indices.push(a, frontCenterIdx, b);
    // classify cap as side (painted with main fuselage color)
    for (let k = 0; k < 3; k++) groupSide.push(base + k);
  }
  const backCenterIdx = positions.length / 3;
  positions.push(0, 0, stations[stations.length - 1].z + 0.02);
  const lastStation = stations.length - 1;
  for (let j = 0; j < N; j++) {
    const a = lastStation * N + j;
    const b = lastStation * N + ((j + 1) % N);
    const base = indices.length;
    indices.push(a, b, backCenterIdx);
    for (let k = 0; k < 3; k++) groupSide.push(base + k);
  }

  // Reorder indices so material groups are contiguous ranges.
  const reordered: number[] = [];
  const bellyStart = reordered.length;
  for (const i of groupBelly) reordered.push(indices[i]);
  const bellyCount = reordered.length - bellyStart;
  const sideStart = reordered.length;
  for (const i of groupSide) reordered.push(indices[i]);
  const sideCount = reordered.length - sideStart;
  const topStart = reordered.length;
  for (const i of groupTop) reordered.push(indices[i]);
  const topCount = reordered.length - topStart;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(reordered);
  geom.addGroup(bellyStart, bellyCount, 0);
  geom.addGroup(sideStart, sideCount, 1);
  geom.addGroup(topStart, topCount, 2);
  geom.computeVertexNormals();

  const mesh = new THREE.Mesh(geom, [bellyMat, sideMat, topMat]);
  grp.add(mesh);
  return grp;
}

/** Airfoil-shaped wing with rounded tip and trailing-edge aileron split hint. */
function makeFokkerWing(span: number, chord: number, thickness: number, mainMat: THREE.Material, edgeMat: THREE.Material): THREE.Group {
  const grp = new THREE.Group();
  // Airfoil cross-section
  const shape = new THREE.Shape();
  const N = 32;
  shape.moveTo(-0.5 * chord, 0);
  for (let i = 1; i <= N; i++) {
    const t = i / N;
    const y = thickness * (0.2969 * Math.sqrt(t) - 0.126 * t - 0.3516 * t * t + 0.2843 * t * t * t - 0.1015 * t * t * t * t);
    shape.lineTo((-0.5 + t) * chord, y);
  }
  for (let i = N - 1; i >= 0; i--) {
    const t = i / N;
    const y = -thickness * 0.55 * (0.2969 * Math.sqrt(t) - 0.126 * t - 0.3516 * t * t + 0.2843 * t * t * t - 0.1015 * t * t * t * t);
    shape.lineTo((-0.5 + t) * chord, y);
  }
  shape.closePath();

  // Extrude along X with rounded tips via gentle taper: we build manually for smooth tips.
  const stations = 28;
  const profPts = shape.getPoints(40);
  const vertsPerStation = profPts.length;
  const positions: number[] = [];
  const indices: number[] = [];
  const halfSpan = span / 2;
  for (let s = 0; s <= stations; s++) {
    const t = s / stations;
    const x = -halfSpan + t * span;
    const tipFactor = Math.sqrt(Math.max(0, 1 - Math.pow((x) / halfSpan, 2) * 0.92));
    for (const p of profPts) {
      positions.push(x, p.y * tipFactor, p.x * tipFactor);
    }
  }
  for (let s = 0; s < stations; s++) {
    for (let i = 0; i < vertsPerStation - 1; i++) {
      const a = s * vertsPerStation + i;
      const b = s * vertsPerStation + i + 1;
      const c = (s + 1) * vertsPerStation + i + 1;
      const d = (s + 1) * vertsPerStation + i;
      indices.push(a, b, c, a, c, d);
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(geom, mainMat);
  grp.add(mesh);
  // Trailing-edge trim strip (darker)
  const edge = new THREE.Mesh(new THREE.BoxGeometry(span * 0.92, thickness * 0.95, chord * 0.14, 48, 2, 2), edgeMat);
  edge.position.z = chord * 0.43;
  grp.add(edge);
  return grp;
}

/** Detailed propeller blade with a twist — used by the Fokker. */
function makePropBladeGeomF(length: number, maxW: number): THREE.BufferGeometry {
  const stations = 14;
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= stations; i++) {
    const t = i / stations;
    const x = (t - 0.5) * length;
    const w = maxW * Math.sin(Math.PI * t) * 0.95 + maxW * 0.12;
    const twist = (1 - t) * 0.55;
    const th = 0.06;
    positions.push(x, th, -w / 2 + Math.sin(twist) * th);
    positions.push(x, th, w / 2 + Math.sin(twist) * th);
    positions.push(x, -th, w / 2 - Math.sin(twist) * th);
    positions.push(x, -th, -w / 2 - Math.sin(twist) * th);
  }
  for (let i = 0; i < stations; i++) {
    const b = i * 4;
    for (let k = 0; k < 4; k++) {
      const a = b + k;
      const bb = b + ((k + 1) % 4);
      const c = b + 4 + ((k + 1) % 4);
      const d = b + 4 + k;
      indices.push(a, bb, c, a, c, d);
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return geom;
}

function makeCockpitGeometry(cowlingColor = 0x8a1e1e, wingColor = 0x7a1818): THREE.Group {
  const g = new THREE.Group();
  // The camera sits at local (0, 0.6, -0.3). Everything below lives in the same parent frame.
  // We want a substantial chunk of airframe visible at the lower ~35% of the view.

  // Engine cowling — sits LOW and forward so it reads as the nose framing the bottom of the
  // view, leaving the centre clear for aiming (it used to fill the whole middle).
  const cowlingMat = new THREE.MeshStandardMaterial({ color: cowlingColor, roughness: 0.55, metalness: 0.15 });
  const cowling = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.8, 1.3, 48, 3), cowlingMat);
  cowling.rotation.x = Math.PI / 2;
  cowling.position.set(0, -0.82, -2.0);
  g.add(cowling);

  // Prop disc — a translucent spinning-blade blur (see-through, rotates each frame), low and ahead.
  // 1.5 radius (50% wider than the 1.0 baseline) to match the Fokker's big rotary prop.
  const prop = makePropDisc(1.5);
  prop.position.set(0, -0.82, -2.9);
  g.add(prop);

  // Spinner cap
  const spinnerMat = new THREE.MeshStandardMaterial({ color: 0x5a1010, roughness: 0.5, metalness: 0.2 });
  const spinner = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.4, 40, 3), spinnerMat);
  spinner.rotation.x = -Math.PI / 2;
  spinner.position.set(0, -0.82, -3.05);
  g.add(spinner);

  // Upper wing — wide enough that its ends run PAST the left/right edges of the view (the Fokker's
  // top wing fills the pilot's overhead frame; a 7u span ended mid-screen and read as cut off).
  const wingMat = new THREE.MeshStandardMaterial({ color: wingColor, roughness: 0.7 });
  // Span 16u so the ends run well past both screen edges (at this depth the visible width is only
  // ~3.5u, so a 7u wing ended mid-frame). Sits high at the top of the view, framing the sky
  // overhead and coming fully into frame as the pilot pitches up — not blocking forward sight.
  const upperWing = new THREE.Mesh(new THREE.BoxGeometry(16, 0.24, 1.8, 40, 2, 4), wingMat);
  upperWing.position.set(0, 1.4, -2.0);
  g.add(upperWing);

  // Struts
  const strutMat = new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.9 });
  for (const dx of [-0.85, 0.85]) {
    const strut = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.9, 24, 4), strutMat);
    strut.position.set(dx, 0.65, -2.0);
    strut.rotation.z = dx > 0 ? -0.28 : 0.28; // lean in at the bottom, toward the fuselage
    g.add(strut);
  }

  // Twin Spandau MGs on the cowl deck — the signature foreground of a Fokker cockpit — plus the
  // shared varnished-wood instrument panel with brass dial gauges + leather coaming. Both sit in
  // the lower-middle of the view, clear of the HUD's top banner and bottom jump/boost bars.
  const bodyMat = new THREE.MeshStandardMaterial({ color: cowlingColor, roughness: 0.6, metalness: 0.1 });
  cockpitGuns(g, -0.18, -1.45, 0.3);
  cockpitDash(g, bodyMat, -0.6);

  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}
